<template>
  <FormContainer withoutDivider>
    <template #title>Informations salarié(e)</template>
    <template #content>
      <div
        class="form__bloc"
        v-if="!readonly"
      >
        <RechercheSalarie
          mode="salaries"
          :id-demande="model!.id"
          @selected="onRechercheSalarie"
          class="col-12 col-md-18 col-sm-36"
          :readonly
        />
      </div>

      <div class="form__bloc">
        <WrapperInput
          id="field_123"
          :model-value="salariéEnCours?.nomNaissance"
          @update:model-value="(value: any) => updateModule('nomNaissance', activeSalariéIndex!, value)"
          label="Nom de naissance"
          hint="50 caractères maximum"
          class="col-12 col-md-18 col-sm-36"
          :placeholder="PLACEHOLDER_NOM"
          :required="metaSalarieEnCours?.nomNaissance?.$status === PropertyStatus.Required"
          :alertLabel="metaSalarieEnCours?.nomNaissance?.$messageErreurDirty!"
          :maxlength="50"
          deletable
          :readonly
        />
        <WrapperInput
          id="field_124"
          :model-value="salariéEnCours?.nom"
          @update:model-value="(value: any) => updateModule('nom', activeSalariéIndex!, value)"
          label="Nom d'usage (si différent)"
          hint="50 caractères maximum"
          class="col-12 col-md-18 col-sm-36"
          :placeholder="PLACEHOLDER_NOM_NAIS"
          :required="metaSalarieEnCours?.nom?.$status === PropertyStatus.Required"
          :alertLabel="metaSalarieEnCours?.nom?.$messageErreurDirty!"
          :maxlength="50"
          deletable
          :readonly
        />
        <WrapperInput
          id="field_125"
          :model-value="salariéEnCours?.prenom"
          @update:model-value="(value: any) => updateModule('prenom', activeSalariéIndex!, value)"
          label="Prénom"
          hint="50 caractères maximum"
          class="col-12 col-md-18 col-sm-36"
          :placeholder="PLACEHOLDER_PRENOM"
          :required="metaSalarieEnCours?.prenom?.$status === PropertyStatus.Required"
          :alertLabel="metaSalarieEnCours?.prenom?.$messageErreurDirty!"
          :maxlength="50"
          deletable
          :readonly
        />
      </div>
      <div class="form__bloc">
        <WrapperInputDate
          id="field_126"
          :model-value="salariéEnCours?.dateNaissance"
          @update:model-value="(value: any) => updateModule('dateNaissance', activeSalariéIndex!, value)"
          label="Date de naissance"
          :hint="HINT_DATE"
          class="col-8 col-md-12 col-sm-36"
          :placeholder="PLACEHOLDER_DATE_NAIS"
          deletable
          :required="metaSalarieEnCours?.dateNaissance?.$status === PropertyStatus.Required"
          :alertLabel="metaSalarieEnCours?.dateNaissance?.$messageErreurDirty!"
          :readonly
        />
        <WrapperDropdown
          id="field_127"
          key="sexe-dropdown"
          :model-value="salariéEnCours?.sexe"
          @update:model-value="(value: any) => updateModule('sexe', activeSalariéIndex!, value)"
          label="Sexe"
          class="col-7 col-md-12 col-sm-36"
          :items="sexeItems"
          :readonly
          :required="metaSalarieEnCours?.sexe?.$status === PropertyStatus.Required"
          :alertLabel="metaSalarieEnCours?.sexe?.$messageErreurDirty!"
        />
      </div>
      <div class="form__bloc">
        <WrapperToggleSwitch
          id="field_128"
          :model-value="salariéEnCours?.nirTemporaire"
          @update:model-value="(value: any) => updateModule('nirTemporaire', activeSalariéIndex!, value)"
          label="Êtes-vous détenteur d'un NIR temporaire ?"
          :required="metaSalarieEnCours?.nirTemporaire?.$status === PropertyStatus.Required"
          :alertLabel="metaSalarieEnCours?.nirTemporaire?.$messageErreurDirty!"
          :items="OuiOuNon"
          :readonly
          class="col-10 col-md-14 col-sm-36"
          help="Le numéro technique temporaire est un numéro attribué à l'individu par son employeur dans l'attente de la délivrance d'un NIA puis d'un NIR définitif par la sécurité sociale"
        />
        <WrapperInput
          id="field_129"
          :model-value="salariéEnCours?.nir"
          @update:model-value="(value: any) => updateModule('nir', activeSalariéIndex!, value)"
          :label="nirLabel"
          class="col-10 col-md-12 col-sm-36"
          placeholder="Ex : 1234567890123"
          :required="metaSalarieEnCours?.nir?.$status === PropertyStatus.Required"
          :alertLabel="metaSalarieEnCours?.nir?.$messageErreurDirty!"
          :maxlength="nirMaxLength"
          deletable
          :readonly
          :hint="nirHint"
          :help="nirHelp"
        />
        <WrapperToggleSwitch
          id="field_130"
          :model-value="salariéEnCours?.beneficieReconnaissanceTravailleurHandicape"
          @update:model-value="
            (value: any) => updateModule('beneficieReconnaissanceTravailleurHandicape', activeSalariéIndex!, value)
          "
          label="Qualité de travailleur handicapé"
          :items="OuiOuNon"
          :required="
            metaSalarieEnCours?.beneficieReconnaissanceTravailleurHandicape?.$status === PropertyStatus.Required
          "
          :alertLabel="metaSalarieEnCours?.beneficieReconnaissanceTravailleurHandicape?.$messageErreurDirty!"
          :readonly
          class="col-10 col-md-14 col-sm-36"
        />
      </div>
    </template>
  </FormContainer>
</template>

<script setup lang="ts">
  import { computed, inject } from 'vue'
  import { storeToRefs } from 'pinia'
  import { useDemandeAFStore } from '@/features/interface/demande/af/useDemandeAFStore'
  import { SalarieActionKey, type SalarieAction } from '@/features/interface/demande/af/steps/salarie/useSalarieActions'
  import { PropertyStatus } from '@/features/domaine/property-status/PropertyStatus'
  import {
    FormContainer,
    HINT_DATE,
    PLACEHOLDER_DATE_NAIS,
    PLACEHOLDER_NOM,
    PLACEHOLDER_NOM_NAIS,
    PLACEHOLDER_PRENOM,
  } from '@/features/interface/demande/shared'
  import type { SalarieDTO } from '@/features/api/extranet/dto/Demande/AF'
  import { sexeItems, OuiOuNon, objectIsEmpty } from '@/features/interface/shared/tools'
  import { RechercheSalarie } from '@/features/interface/demande/shared/components/salaries'
  import type { SalarieRechercheDTO } from '@/features/api/extranet/dto/Demande/shared'
  import { useDialog, type DialogResult } from '@/features/interface/dialog'

  const store = useDemandeAFStore()
  const { setupArrayUpdaterWithKey } = store

  const { readonly, model } = storeToRefs(store)
  const { activeSalariéIndex, salariéEnCours, metaSalarieEnCours, patchRemplacerSalarié } =
    inject<SalarieAction>(SalarieActionKey)!

  const updateModule = setupArrayUpdaterWithKey<SalarieDTO>('salaries')

  const onRechercheSalarie = async (value: SalarieRechercheDTO) => {
    let reponse: DialogResult = 'Ok'
    if (objectIsEmpty(salariéEnCours.value, { exclude: ['source'] }) === false) {
      const dialog = useDialog()
      reponse = await dialog.showDialog({
        message: ['Cette opération entrainera le remplacement de la saisie existante.', "Confirmez-vous l'opération ?"],
        type: 'OkCancel',
        closable: true,
        title: 'Attention',
      })
    }
    if (reponse === 'Ok') {
      patchRemplacerSalarié(value)
    }
  }

  const nirMaxLength = computed(() => {
    return salariéEnCours?.value?.nirTemporaire === true ? 40 : 13
  })

  const nirHint = computed(() => {
    return salariéEnCours?.value?.nirTemporaire === true ? '11 à 40 caractères' : '13 caractères'
  })

  const nirHelp = computed(() => {
    return salariéEnCours?.value?.nirTemporaire === true
      ? "Le numéro technique temporaire est un numéro attribué à l'individu par son employeur dans l'attente de la délivrance d'un NIA puis d'un NIR définitif par la sécurité sociale."
      : 'Le numéro de sécurité sociale est inscrit sur la carte vitale, en dessous du nom et du prénom du porteur.'
  })

  const nirLabel = computed(() => {
    return salariéEnCours?.value?.nirTemporaire === true
      ? 'Numéro technique temporaire'
      : 'Numéro de sécurité sociale (NIR)'
  })
</script>
